// src/App.tsx - ランキング・掲示板機能追加版
import React, { useState, useEffect } from 'react';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import { Box, Tabs, Tab, CssBaseline, Snackbar, Alert, CircularProgress, IconButton, Typography } from '@mui/material';
import { Dashboard, BarChart, ShowChart, AttachMoney, Settings, ExitToApp, EmojiEvents, Forum, CalendarMonth, HelpOutline } from '@mui/icons-material';
import DashboardScreen from './screens/DashboardScreen';
import HistoryScreen from './screens/HistoryScreen';
import ChartsScreen from './screens/ChartsScreen';
import CalendarScreen from './screens/CalendarScreen';
import ExpenseScreen from './screens/ExpenseScreen';
import SettingsScreen from './screens/SettingsScreen';
import RankingScreen from './screens/RankingScreen';
import BoardScreen from './screens/BoardScreen'; // 新規追加
import OnboardingTutorial from './components/OnboardingTutorial';
import UpdateAnnouncementDialog from './components/UpdateAnnouncementDialog';
import LoginScreen from './screens/LoginScreen';
import TermsScreen from './screens/TermsScreen';
import { onAuthStateChange, logout, getUserFromToken } from './services/auth';
import { initializeDefaultData } from './services/dataService';
import { checkTermsAgreement, agreeToTerms } from './services/terms';
import { trackAnalyticsEvent } from './services/firebase';

const analyticsPageNames = [
  'home',
  'history',
  'analysis',
  'calendar',
  'expense',
  'ranking',
  'board',
  'settings'
];

const darkTheme = createTheme({
  palette: {
    mode: 'dark',
    primary: {
      main: '#10B981',
    },
    background: {
      default: '#0b0f0d',
      paper: '#151a17',
    },
    text: {
      primary: '#ffffff',
      secondary: '#a7b0aa',
    },
  },
  shape: {
    borderRadius: 12,
  },
  typography: {
    fontFamily: [
      'Inter',
      'system-ui',
      '-apple-system',
      'BlinkMacSystemFont',
      '"Segoe UI"',
      'sans-serif',
    ].join(','),
    fontSize: 16,
    body1: {
      fontSize: '1rem',
      lineHeight: 1.55,
    },
    body2: {
      fontSize: '0.95rem',
      lineHeight: 1.5,
    },
    caption: {
      fontSize: '0.85rem',
      lineHeight: 1.45,
    },
    button: {
      fontSize: '0.95rem',
    },
  },
  components: {
    MuiCssBaseline: {
      styleOverrides: {
        body: {
          fontSize: '16px',
          WebkitTextSizeAdjust: '100%',
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          backgroundImage: 'none',
          border: '1px solid rgba(255,255,255,0.06)',
          boxShadow: '0 10px 30px rgba(0,0,0,0.18)',
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        root: {
          textTransform: 'none',
          fontWeight: 700,
          fontSize: '0.95rem',
        },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: {
          fontSize: '0.86rem',
          minHeight: 30,
        },
        sizeSmall: {
          fontSize: '0.8rem',
          minHeight: 26,
        },
      },
    },
    MuiTab: {
      styleOverrides: {
        root: {
          fontSize: '0.88rem',
          fontWeight: 700,
        },
      },
    },
    MuiInputBase: {
      styleOverrides: {
        root: {
          fontSize: '1rem',
        },
      },
    },
  },
});

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;
  return (
    <div role="tabpanel" hidden={value !== index} {...other}>
      {value === index && <Box sx={{ p: 0 }}>{children}</Box>}
    </div>
  );
}

function App() {
  const [user, setUser] = useState<any>(null);
  const [authLoading, setAuthLoading] = useState(true);
  const [hasAgreedToTerms, setHasAgreedToTerms] = useState(false);
  const [termsLoading, setTermsLoading] = useState(false);
  const [selectedTab, setSelectedTab] = useState(0);
  const [tutorialOpen, setTutorialOpen] = useState(false);
  const [updateAnnouncementOpen, setUpdateAnnouncementOpen] = useState(false);
  const [simpleMode, setSimpleMode] = useState(() => {
    return localStorage.getItem('delivery-tracker-simple-mode') === 'true';
  });
  const [snackbar, setSnackbar] = useState<{
    open: boolean;
    message: string;
    severity: 'success' | 'error';
  }>({ open: false, message: '', severity: 'success' });

  const tutorialStorageKey = user?.uid
    ? `delivery-tracker-onboarding-v2026-05-calendar-${user.uid}`
    : null;
  const updateAnnouncementStorageKey = user?.uid
    ? `delivery-tracker-update-v2026-06-03-analysis-simple-${user.uid}`
    : null;

  useEffect(() => {
    const initAuth = async () => {
      const sessionDataStr = localStorage.getItem('authSession');
      if (sessionDataStr) {
        try {
          const { token, loginTime, expiresIn } = JSON.parse(sessionDataStr);
          const now = Date.now();
          if (now - loginTime > expiresIn) {
            localStorage.removeItem('authSession');
            setSnackbar({ open: true, message: 'セッションが期限切れになりました。再度ログインしてください', severity: 'error' });
            setAuthLoading(false);
            return;
          }
          const currentUser = await getUserFromToken(token);
          if (currentUser) {
            setUser(currentUser);
            setTermsLoading(true);
            const agreed = await checkTermsAgreement(currentUser.uid);
            setHasAgreedToTerms(agreed);
            setTermsLoading(false);
            
            if (agreed) {
              initializeDefaultData();
            }
            setAuthLoading(false);
            return;
          } else {
            localStorage.removeItem('authSession');
          }
        } catch (error) {
          console.error('Session parse error:', error);
          localStorage.removeItem('authSession');
        }
      }

      const unsubscribe = onAuthStateChange(async (currentUser) => {
        setUser(currentUser);
        
        if (currentUser) {
          setTermsLoading(true);
          const agreed = await checkTermsAgreement(currentUser.uid);
          setHasAgreedToTerms(agreed);
          setTermsLoading(false);
          
          if (agreed) {
            initializeDefaultData();
          }
        } else {
          setHasAgreedToTerms(false);
        }
        
        setAuthLoading(false);
      });

      return () => unsubscribe();
    };

    initAuth();
  }, []);

  const handleTabChange = (_event: React.SyntheticEvent, newValue: number) => {
    setSelectedTab(newValue);
  };

  useEffect(() => {
    if (!user || !hasAgreedToTerms) return;
    const screenName = analyticsPageNames[selectedTab] || 'unknown';
    trackAnalyticsEvent('screen_view', {
      firebase_screen: screenName,
      firebase_screen_class: 'main_tab'
    });
  }, [hasAgreedToTerms, selectedTab, user]);

  useEffect(() => {
    if (!tutorialStorageKey || !user || !hasAgreedToTerms || authLoading || termsLoading) return;
    if (!localStorage.getItem(tutorialStorageKey)) {
      setTutorialOpen(true);
    }
  }, [authLoading, hasAgreedToTerms, termsLoading, tutorialStorageKey, user]);

  useEffect(() => {
    if (
      !updateAnnouncementStorageKey ||
      !user ||
      !hasAgreedToTerms ||
      authLoading ||
      termsLoading ||
      tutorialOpen
    ) {
      return;
    }
    if (!localStorage.getItem(updateAnnouncementStorageKey)) {
      setUpdateAnnouncementOpen(true);
    }
  }, [authLoading, hasAgreedToTerms, termsLoading, tutorialOpen, updateAnnouncementStorageKey, user]);

  const closeTutorial = () => {
    if (tutorialStorageKey) {
      localStorage.setItem(tutorialStorageKey, 'seen');
    }
    setTutorialOpen(false);
  };

  const openTutorial = () => {
    setTutorialOpen(true);
  };

  const closeUpdateAnnouncement = () => {
    if (updateAnnouncementStorageKey) {
      localStorage.setItem(updateAnnouncementStorageKey, 'seen');
    }
    setUpdateAnnouncementOpen(false);
  };

  const showSnackbar = (message: string, severity: 'success' | 'error' = 'success') => {
    setSnackbar({ open: true, message, severity });
  };

  const closeSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  const handleSimpleModeChange = (checked: boolean) => {
    setSimpleMode(checked);
    localStorage.setItem('delivery-tracker-simple-mode', checked ? 'true' : 'false');
    trackAnalyticsEvent('simple_mode_change', { enabled: checked });
    showSnackbar(checked ? 'シンプルモードをオンにしました' : 'シンプルモードをオフにしました');
  };

  const pageGuides = [
    {
      title: 'ホーム',
      description: '今日の売上・目標・サービス別の状況をすばやく確認できます。記録追加は右下のボタンから行えます。'
    },
    {
      title: '履歴',
      description: '過去の配達記録を確認するページです。カードの詳細を開くと、編集・削除・稼働時間の確認ができます。'
    },
    {
      title: '分析',
      description: '期間を選んで、売上・利益・時給・支出・サービス別の強みを確認できます。タブは横にスライドできます。'
    },
    {
      title: 'カレンダー',
      description: '月ごとの売上・支出・利益を日付別に見られます。日付を押すと、その日の内訳を確認できます。'
    },
    {
      title: '支出',
      description: 'ガソリン代や備品などの経費を記録できます。カテゴリ別に見ると、手残りを減らしている支出がわかります。'
    },
    {
      title: 'ランキング',
      description: '公開設定をした人だけが参加できます。自分の売上や時給を、同じ期間のランキングで確認できます。'
    },
    {
      title: '掲示板',
      description: '配達員同士で情報交換する場所です。地域情報、質問、注意喚起などを投稿できます。'
    },
    {
      title: '設定',
      description: '目標金額、休日、プラットフォーム、カテゴリなどを調整できます。使い始める前にここを整えると集計が見やすくなります。'
    }
  ];

  const currentGuide = pageGuides[selectedTab] || pageGuides[0];

  const handleLogout = async () => {
    localStorage.removeItem('authSession');
    const result = await logout();
    if (result.success) {
      showSnackbar('ログアウトしました', 'success');
      setUser(null);
      setHasAgreedToTerms(false);
    } else {
      showSnackbar('ログアウトに失敗しました', 'error');
    }
  };

  const handleAgreeToTerms = async () => {
    if (!user) return;
    
    setTermsLoading(true);
    const result = await agreeToTerms(user.uid);
    setTermsLoading(false);
    
    if (result.success) {
      setHasAgreedToTerms(true);
      initializeDefaultData();
      showSnackbar('利用規約に同意しました', 'success');
    } else {
      showSnackbar(result.error || '規約同意の保存に失敗しました', 'error');
    }
  };

  if (authLoading || termsLoading) {
    return (
      <ThemeProvider theme={darkTheme}>
        <CssBaseline />
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            height: '100vh',
            bgcolor: 'black'
          }}
        >
          <CircularProgress sx={{ color: '#10B981' }} size={48} />
        </Box>
      </ThemeProvider>
    );
  }

  if (!user) {
    return (
      <ThemeProvider theme={darkTheme}>
        <CssBaseline />
        <LoginScreen
          onLoginSuccess={() => {}}
          onShowSnackbar={showSnackbar}
        />
        <Snackbar
          open={snackbar.open}
          autoHideDuration={3000}
          onClose={closeSnackbar}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        >
          <Alert onClose={closeSnackbar} severity={snackbar.severity} sx={{ width: '100%' }}>
            {snackbar.message}
          </Alert>
        </Snackbar>
      </ThemeProvider>
    );
  }

  if (!hasAgreedToTerms) {
    return (
      <ThemeProvider theme={darkTheme}>
        <CssBaseline />
        <TermsScreen onAgree={handleAgreeToTerms} />
        <Snackbar
          open={snackbar.open}
          autoHideDuration={3000}
          onClose={closeSnackbar}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        >
          <Alert onClose={closeSnackbar} severity={snackbar.severity} sx={{ width: '100%' }}>
            {snackbar.message}
          </Alert>
        </Snackbar>
      </ThemeProvider>
    );
  }

  return (
    <ThemeProvider theme={darkTheme}>
      <CssBaseline />
      <Box sx={{ display: 'flex', flexDirection: 'column', height: '100vh', bgcolor: '#0b0f0d' }}>
        <Box
          sx={{
            bgcolor: 'rgba(15, 20, 18, 0.96)',
            borderBottom: '1px solid rgba(255,255,255,0.08)',
            boxShadow: '0 6px 24px rgba(0,0,0,0.22)',
            zIndex: 2,
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', px: 2, py: 1 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Box
                component="img"
                src="/assets/app-icon.png"
                alt="Delivery Tracker"
                sx={{
                  width: 34,
                  height: 34,
                  objectFit: 'contain',
                  filter: 'drop-shadow(0 2px 8px rgba(0,0,0,0.25))'
                }}
              />
              <Box sx={{ minWidth: 0 }}>
                <Box fontSize={15} fontWeight={700} color="#fff" lineHeight={1.2}>
                  Delivery Tracker
                </Box>
                <Box fontSize={13} color="#8b9690">
                  {user.displayName || user.email}
                </Box>
              </Box>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <IconButton
                onClick={openTutorial}
                sx={{ color: '#888', '&:hover': { color: '#10B981' } }}
                size="small"
                aria-label="使い方ガイドを開く"
              >
                <HelpOutline fontSize="small" />
              </IconButton>
              <IconButton
                onClick={handleLogout}
                sx={{ color: '#888', '&:hover': { color: '#10B981' } }}
                size="small"
                aria-label="ログアウト"
              >
                <ExitToApp fontSize="small" />
              </IconButton>
            </Box>
          </Box>
          <Box sx={{ width: '100%' }}>
            <Tabs
              value={selectedTab}
              onChange={handleTabChange}
              variant="scrollable"
              scrollButtons="auto"
              allowScrollButtonsMobile
              textColor="primary"
              indicatorColor="primary"
              sx={{
                width: '100%',
                '& .MuiTab-root': {
                  minHeight: 62,
                  minWidth: { xs: 82, sm: 98 },
                  px: { xs: 1.1, sm: 1.3, md: 1.6 },
                  fontSize: { xs: '12.5px', sm: '13.5px', md: '14px' },
                  color: '#93a09a',
                  letterSpacing: 0,
                  borderRadius: '12px 12px 0 0',
                },
                '& .Mui-selected': {
                  color: '#10B981 !important',
                  bgcolor: 'rgba(16,185,129,0.08)',
                },
                '& .MuiTabs-scrollButtons': {
                  color: '#888888',
                  '&.Mui-disabled': {
                    opacity: 0.3,
                  },
                },
              }}
            >
              <Tab icon={<Dashboard />} label={'\u30db\u30fc\u30e0'} />
              <Tab icon={<BarChart />} label={'\u5c65\u6b74'} />
              <Tab icon={<ShowChart />} label={'\u5206\u6790'} />
              <Tab icon={<CalendarMonth />} label={'\u30ab\u30ec\u30f3\u30c0\u30fc'} />
              <Tab icon={<AttachMoney />} label={'\u652f\u51fa'} />
              <Tab
                icon={
                  <Box sx={{ position: 'relative' }}>
                    <EmojiEvents />
                    <Box
                      sx={{
                        position: 'absolute',
                        top: -4,
                        right: -8,
                        bgcolor: '#EF4444',
                        color: 'white',
                        fontSize: 8,
                        fontWeight: 'bold',
                        px: 0.5,
                        py: 0.2,
                        borderRadius: 1,
                        lineHeight: 1
                      }}
                    >
                      NEW
                    </Box>
                  </Box>
                }
                label={'\u30e9\u30f3\u30ad\u30f3\u30b0'}
                sx={{
                  '&.Mui-selected': {
                    background: 'linear-gradient(135deg, #10B98120 0%, #10B98140 100%)',
                  }
                }}
              />
              <Tab
                icon={
                  <Box sx={{ position: 'relative' }}>
                    <Forum />
                    <Box
                      sx={{
                        position: 'absolute',
                        top: -4,
                        right: -8,
                        bgcolor: '#EF4444',
                        color: 'white',
                        fontSize: 8,
                        fontWeight: 'bold',
                        px: 0.5,
                        py: 0.2,
                        borderRadius: 1,
                        lineHeight: 1
                      }}
                    >
                      NEW
                    </Box>
                  </Box>
                }
                label={'\u63b2\u793a\u677f'}
                sx={{
                  '&.Mui-selected': {
                    background: 'linear-gradient(135deg, #10B98120 0%, #10B98140 100%)',
                  }
                }}
              />
              <Tab icon={<Settings />} label={'\u8a2d\u5b9a'} />
            </Tabs>
          </Box>
          <Box
            sx={{
              px: { xs: 1.1, sm: 2 },
              py: { xs: 0.85, sm: 1.1 },
              display: 'flex',
              gap: { xs: 0.85, sm: 1 },
              alignItems: 'center',
              borderTop: '1px solid rgba(255,255,255,0.05)',
              bgcolor: 'linear-gradient(135deg, rgba(16,185,129,0.10), rgba(245,158,11,0.05))',
              position: 'relative',
              overflow: 'hidden',
              '&::after': {
                content: '""',
                position: 'absolute',
                right: -34,
                bottom: -54,
                width: 150,
                height: 150,
                borderRadius: '50%',
                background: 'radial-gradient(circle, rgba(16,185,129,0.16) 0%, rgba(16,185,129,0) 70%)',
                pointerEvents: 'none'
              }
            }}
          >
            <Box
              component="img"
              src="/assets/mini-character-transparent.png"
              alt=""
              sx={{
                width: { xs: 48, sm: 56 },
                height: { xs: 58, sm: 64 },
                objectFit: 'contain',
                flexShrink: 0,
                display: 'block',
                filter: 'drop-shadow(0 8px 14px rgba(0,0,0,0.34))',
                transform: 'translateY(2px)',
                zIndex: 1
              }}
            />
            <Box
              sx={{
                flex: 1,
                minWidth: 0,
                zIndex: 1,
                px: 1.15,
                py: 0.85,
                borderRadius: '12px',
                bgcolor: 'rgba(5, 14, 10, 0.48)',
                border: '1px solid rgba(255,255,255,0.06)',
                position: 'relative',
                '&::before': {
                  content: '""',
                  position: 'absolute',
                  left: -6,
                  top: 19,
                  width: 10,
                  height: 10,
                  bgcolor: 'rgba(5, 14, 10, 0.48)',
                  borderLeft: '1px solid rgba(255,255,255,0.06)',
                  borderBottom: '1px solid rgba(255,255,255,0.06)',
                  transform: 'rotate(45deg)'
                }
              }}
            >
              <Typography fontSize={13} fontWeight={800} color="#D1FAE5" lineHeight={1.25}>
                {currentGuide.title}の使いどころ
              </Typography>
              <Typography
                fontSize={12}
                color="#9ca7a0"
                lineHeight={1.45}
                sx={{
                  display: '-webkit-box',
                  WebkitLineClamp: { xs: 2, sm: 3 },
                  WebkitBoxOrient: 'vertical',
                  overflow: 'hidden'
                }}
              >
                {currentGuide.description}
              </Typography>
            </Box>
            <Typography
              fontSize={12}
              fontWeight={800}
              color="#FCD34D"
              sx={{
                flexShrink: 0,
                px: { xs: 0.75, sm: 1 },
                py: 0.4,
                borderRadius: '999px',
                bgcolor: 'rgba(245,158,11,0.12)',
                border: '1px solid rgba(245,158,11,0.20)',
                zIndex: 1,
                maxWidth: { xs: 76, sm: 'none' },
                textAlign: 'center',
                lineHeight: 1.25
              }}
            >
              ← 横にスライド
            </Typography>
          </Box>
        </Box>

        <Box sx={{ flex: 1, overflow: 'auto', bgcolor: '#0b0f0d' }}>
          <TabPanel value={selectedTab} index={0}>
            <DashboardScreen onShowSnackbar={showSnackbar} simpleMode={simpleMode} onSimpleModeChange={handleSimpleModeChange} />
          </TabPanel>
          <TabPanel value={selectedTab} index={1}>
            <HistoryScreen onShowSnackbar={showSnackbar} simpleMode={simpleMode} />
          </TabPanel>
          <TabPanel value={selectedTab} index={2}>
            <ChartsScreen onShowSnackbar={showSnackbar} simpleMode={simpleMode} />
          </TabPanel>
          <TabPanel value={selectedTab} index={3}>
            <CalendarScreen onShowSnackbar={showSnackbar} />
          </TabPanel>
          <TabPanel value={selectedTab} index={4}>
            <ExpenseScreen onShowSnackbar={showSnackbar} />
          </TabPanel>
          <TabPanel value={selectedTab} index={5}>
            <RankingScreen onShowSnackbar={showSnackbar} simpleMode={simpleMode} />
          </TabPanel>
          <TabPanel value={selectedTab} index={6}>
            <BoardScreen onShowSnackbar={showSnackbar} /> {/* 新規追加 */}
          </TabPanel>
          <TabPanel value={selectedTab} index={7}>
            <SettingsScreen onShowSnackbar={showSnackbar} />
          </TabPanel>
        </Box>

        <Snackbar
          open={snackbar.open}
          autoHideDuration={3000}
          onClose={closeSnackbar}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        >
          <Alert onClose={closeSnackbar} severity={snackbar.severity} sx={{ width: '100%' }}>
            {snackbar.message}
          </Alert>
        </Snackbar>

        <OnboardingTutorial
          open={tutorialOpen}
          onClose={closeTutorial}
        />
        <UpdateAnnouncementDialog
          open={updateAnnouncementOpen}
          onClose={closeUpdateAnnouncement}
        />
      </Box>
    </ThemeProvider>
  );
}

export default App;

